import React from 'react';
import { BookOpen, Calculator, TrendingUp, Award, Sparkles, Target, BarChart3, FileText, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AdminLayout } from './AdminDashboard';

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
  <Card className="mb-6" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-heading flex items-center gap-2 text-primary">
        <Icon className="h-5 w-5" />
        {title}
      </CardTitle>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const Formula = ({ children }) => (
  <div className="bg-slate-900 text-emerald-200 font-mono text-sm rounded-lg p-4 my-2 overflow-x-auto" data-testid="formula-block">
    {children}
  </div>
);

const Badgeline = ({ items }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {items.map((it, i) => (
      <Badge key={i} className={it.cls}>{it.label}</Badge>
    ))}
  </div>
);

const BenchmarkRow = ({ band, color, range, label, note }) => (
  <tr className="border-b last:border-0">
    <td className="py-2 pl-3"><span className={`inline-block w-2 h-2 rounded-full mr-2 align-middle ${color}`} /><span className="font-medium text-sm">{band}</span></td>
    <td className="py-2 text-sm text-muted-foreground">{range}</td>
    <td className="py-2 text-sm">{label}</td>
    <td className="py-2 pr-3 text-xs text-muted-foreground italic">{note}</td>
  </tr>
);

const Methodology = () => {
  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-5xl mx-auto" data-testid="methodology-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary mb-2 flex items-center gap-3">
            <BookOpen className="h-8 w-8" />
            Methodology &amp; Industry Benchmarks
          </h1>
          <p className="text-sm text-muted-foreground">
            Reference guide for how every score, weight, and metric in this portal is calculated — with hospitality industry benchmarks for context.
          </p>
        </div>

        {/* 1. Question Scoring */}
        <Section icon={Calculator} title="1. Question-Level Scoring" subtitle="How every question type converts to a 0–5 score" testId="section-question-scoring">
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-semibold">Rating questions (1–5 scale)</p>
              <p className="text-muted-foreground">Score = the rating chosen by the employee, used as-is.</p>
              <Formula>question_avg = sum(ratings) / count(ratings)</Formula>
            </div>
            <div>
              <p className="font-semibold">Yes / No questions</p>
              <p className="text-muted-foreground">&quot;Yes&quot; maps to 5, &quot;No&quot; maps to 1 for scoring.</p>
              <Formula>yes_no_avg = (5 × #Yes + 1 × #No) / total_responses</Formula>
            </div>
            <div>
              <p className="font-semibold">Multi-select questions</p>
              <p className="text-muted-foreground">Each option has a configurable score. For a single response that selects multiple options, the per-response score is the mean of the selected option scores.</p>
              <Formula>response_score = mean(option_scores_for_selected) <br/>question_avg = mean(response_scores)</Formula>
            </div>
            <div>
              <p className="font-semibold">Open-ended (comments)</p>
              <p className="text-muted-foreground"><em>Informational only</em> — not scored. Counted by number of meaningful responses (NA / OK / single-char entries are filtered as junk).</p>
            </div>
          </div>
        </Section>

        {/* 2. Section Scores */}
        <Section icon={BarChart3} title="2. Section-Level Score" subtitle="Average of all scored questions in a section" testId="section-section-scores">
          <p className="text-sm text-muted-foreground">A section&apos;s score is the simple arithmetic mean of its scored questions (rating, yes/no, multi-select). Open-ended questions are excluded from this calculation.</p>
          <Formula>section_avg = sum(question_avg for q in section) / count(scored_questions_in_section)</Formula>
        </Section>

        {/* 3. Section Weights & Composite Groups */}
        <Section icon={Target} title="3. Section Weights &amp; Composite Group Scores" subtitle="Weighted means for grouped engagement themes" testId="section-weights">
          <div className="space-y-3 text-sm">
            <p>
              In Survey Builder you can assign a numeric <strong>weight</strong> to each section (default = 1) and group sections into <strong>Composite Groups</strong> (e.g., &quot;Basic Needs&quot;, &quot;Management Support&quot;, &quot;Teamwork&quot;, &quot;Growth&quot;). The composite score for a group is the <strong>weighted mean</strong> of the included sections.
            </p>
            <Formula>group_score = sum(section_avg<sub>i</sub> × weight<sub>i</sub>) / sum(weight<sub>i</sub>)</Formula>
            <div className="bg-muted/40 rounded-lg p-3 text-xs">
              <p className="font-semibold mb-1">Example</p>
              <p>&quot;Management Support&quot; group includes:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>&quot;My supervisor cares about me&quot; — avg 3.8, weight 2</li>
                <li>&quot;Someone encourages my development&quot; — avg 3.4, weight 1</li>
              </ul>
              <p className="mt-1 font-mono">= (3.8×2 + 3.4×1) / (2+1) = 11.0 / 3 = <strong>3.67</strong></p>
            </div>
            <p className="text-muted-foreground">
              Heavier weights signal higher importance — used when management wants certain questions to carry more influence in the composite (e.g., &quot;trust in supervisor&quot; weighted more than &quot;cafeteria quality&quot;).
            </p>
          </div>
        </Section>

        {/* 4. Overall Score */}
        <Section icon={Award} title="4. Overall Engagement Score" subtitle="The headline number on the Analytics page" testId="section-overall">
          <p className="text-sm">
            The Overall Score is the average of every individual response&apos;s <em>average_score</em>, which itself is the mean of all scored answers that respondent provided.
          </p>
          <Formula>overall_avg = mean(response.average_score for all responses)</Formula>
          <Badgeline items={[
            { label: '< 2.5 Critical', cls: 'bg-rose-100 text-rose-800' },
            { label: '2.5–3.5 Average', cls: 'bg-amber-100 text-amber-800' },
            { label: '3.5–4.5 Good', cls: 'bg-emerald-100 text-emerald-800' },
            { label: '≥ 4.5 Excellent', cls: 'bg-emerald-600 text-white' },
          ]} />
        </Section>

        {/* 5. eNPS */}
        <Section icon={TrendingUp} title="5. Employee Net Promoter Score (eNPS)" subtitle="A measure of advocacy strength among employees" testId="section-enps">
          <div className="space-y-3 text-sm">
            <p>
              eNPS classifies <strong>every rating answer</strong> (across all rating questions in the survey) into one of three buckets:
            </p>
            <ul className="list-disc list-inside text-sm space-y-1 pl-2">
              <li><span className="font-semibold text-emerald-700">Promoters</span> — rating = 5/5 (very satisfied, vocal advocates)</li>
              <li><span className="font-semibold text-amber-700">Passives</span> — rating = 4/5 (satisfied but not actively promoting)</li>
              <li><span className="font-semibold text-rose-700">Detractors</span> — rating 1–3 (concerned or actively dissatisfied)</li>
            </ul>
            <Formula>eNPS = %Promoters − %Detractors</Formula>
            <p className="text-muted-foreground">
              Resulting score ranges from −100 to +100. Higher is better.
            </p>
            <div className="bg-muted/40 rounded-lg p-3 text-xs">
              <p className="font-semibold mb-1">Example</p>
              <p>Of 100 rating answers: 30 are 5/5, 50 are 4/5, 20 are 1–3.</p>
              <p className="font-mono mt-1">eNPS = 30% − 20% = <strong>+10</strong> (acceptable, room to grow)</p>
            </div>

            <p className="font-semibold mt-3">eNPS Industry Bands (Hospitality)</p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-left">
                <thead className="bg-muted/50">
                  <tr><th className="py-2 pl-3 text-xs font-semibold">Band</th><th className="py-2 text-xs font-semibold">Range</th><th className="py-2 text-xs font-semibold">Interpretation</th><th className="py-2 pr-3 text-xs font-semibold">Hospitality Reference</th></tr>
                </thead>
                <tbody>
                  <BenchmarkRow band="Needs Work" color="bg-rose-500" range="< 0" label="Negative — more detractors than promoters" note="Action plan required" />
                  <BenchmarkRow band="Acceptable" color="bg-amber-500" range="0 to 10" label="Workable baseline" note="Common for unrecognised hospitality brands" />
                  <BenchmarkRow band="Good" color="bg-emerald-400" range="10 to 30" label="Above-average engagement" note="Typical for well-run hotel groups" />
                  <BenchmarkRow band="Excellent" color="bg-emerald-600" range="30 to 50" label="Strong advocacy culture" note="Top-quartile hospitality employers" />
                  <BenchmarkRow band="World-class" color="bg-violet-600" range="≥ 50" label="Industry leader" note="Top global hospitality employers" />
                </tbody>
              </table>
            </div>
          </div>
        </Section>

        {/* 6. Engagement Buckets */}
        <Section icon={Sparkles} title="6. Engagement Bucket Breakdown" subtitle="How responses are categorised on the dashboard" testId="section-engagement">
          <p className="text-sm">Each individual response is bucketed by its <em>average_score</em>:</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200">
              <p className="font-semibold text-emerald-800 text-sm">Engaged</p>
              <p className="text-xs text-emerald-700 mt-1">avg ≥ 4.0 — committed, motivated</p>
            </div>
            <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
              <p className="font-semibold text-amber-800 text-sm">Not Engaged</p>
              <p className="text-xs text-amber-700 mt-1">2.5 ≤ avg &lt; 4.0 — present but disengaged</p>
            </div>
            <div className="rounded-lg p-3 bg-rose-50 border border-rose-200">
              <p className="font-semibold text-rose-800 text-sm">Actively Disengaged</p>
              <p className="text-xs text-rose-700 mt-1">avg &lt; 2.5 — unhappy, retention risk</p>
            </div>
          </div>
        </Section>

        {/* 7. Participation */}
        <Section icon={BarChart3} title="7. Participation Rate" subtitle="How completion is measured" testId="section-participation">
          <p className="text-sm">Participation is computed against the <strong>current active employee count</strong> in Employee Master at the time the analytics are pulled.</p>
          <Formula>participation_rate = (total_responses / total_active_employees) × 100</Formula>
          <p className="text-sm text-muted-foreground">Responses from employees who have since been deactivated are still counted in the numerator (snapshot preserved) but the denominator uses the active list.</p>
        </Section>

        {/* 8. Anonymity */}
        <Section icon={Target} title="8. Anonymity Threshold (k = 5)" subtitle="Privacy protection for small teams" testId="section-anonymity">
          <p className="text-sm">
            To prevent identifying individual employees in small departments, any department with <strong>fewer than 5 responses</strong> is automatically hidden from breakdowns shown to HR Admins. The total participation rate still reflects every response — only the per-department drill-down is suppressed.
          </p>
          <Badgeline items={[
            { label: 'HR Admin: k = 5 applies', cls: 'bg-amber-100 text-amber-800' },
            { label: 'Super Admin: k = 5 applies', cls: 'bg-amber-100 text-amber-800' },
            { label: 'Sr Management: no threshold for assigned surveys', cls: 'bg-emerald-100 text-emerald-800' },
          ]} />
        </Section>

        {/* 9. Industry Benchmarks — Engagement Score */}
        <Section icon={Award} title="9. Engagement Score Benchmarks (Hospitality)" subtitle="Reference values for the overall 0–5 score" testId="section-benchmarks">
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-muted/50">
                <tr><th className="py-2 pl-3 text-xs font-semibold">Band</th><th className="py-2 text-xs font-semibold">Score</th><th className="py-2 text-xs font-semibold">Meaning</th><th className="py-2 pr-3 text-xs font-semibold">Hospitality Norm</th></tr>
              </thead>
              <tbody>
                <BenchmarkRow band="Critical" color="bg-rose-500" range="< 2.5" label="Major disengagement" note="Below 25th percentile in hotels" />
                <BenchmarkRow band="Average" color="bg-amber-500" range="2.5 to 3.5" label="Survival mode" note="Median-range for unrated brands" />
                <BenchmarkRow band="Good" color="bg-emerald-400" range="3.5 to 4.0" label="Healthy engagement" note="Hospitality industry mean ≈ 3.6" />
                <BenchmarkRow band="Engaged" color="bg-emerald-600" range="4.0 to 4.5" label="Strong, high-performing teams" note="Top-quartile global hotels" />
                <BenchmarkRow band="Excellent" color="bg-violet-600" range="≥ 4.5" label="Best-place-to-work tier" note="Premier hospitality employers" />
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3 italic">
            Hospitality-industry average engagement score is <strong>~3.6 / 5</strong>; global participation average is <strong>~70%</strong>.
            Scores above 4.0 typically correlate with 21% higher profitability, 41% lower absenteeism, and 59% lower turnover in service-industry studies.
          </p>
        </Section>

        {/* 10. Live data note */}
        <Section icon={FileText} title="10. Data Freshness &amp; Live Master Lookup" subtitle="How department changes propagate" testId="section-freshness">
          <p className="text-sm">
            Every analytics view (dashboard, charts, exports, PDF) resolves an employee&apos;s <strong>department from the current Employee Master</strong>, not from the snapshot stored at survey submission. This means whenever HR corrects or updates an employee&apos;s department, all reports immediately reflect the change — without needing to re-submit responses.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            For employees who&apos;ve been deleted from Employee Master, the response snapshot is used as fallback so historical data is never lost.
          </p>
        </Section>

        <div className="text-xs text-muted-foreground text-center mt-8 pb-4">
          <p>Methodology aligned with hospitality industry engagement standards.</p>
          <p>Gnosis Hospitality LLP — Employee Engagement Analytics — v2026.02</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Methodology;
